import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import Section from '../components/ui/Section';
import { toast } from '../utils/toastBus';

const SAMPLE_SCRIPTS = [
  {
    title: 'Morning Coffee Promo',
    text: 'Wake up with the bold taste of River Roast Coffee. Brew better mornings today.'
  },
  {
    title: 'Local Gym Offer',
    text: 'Level up at Pulse Fitness. Join now and get your first month free.'
  },
  {
    title: 'Weekend Sale',
    text: 'Save big this weekend at City Electronics. Deals end Sunday.'
  },
  {
    title: 'Meal Kit Ad',
    text: 'Dinner made simple with FreshCrate. Chef-designed recipes delivered weekly.'
  },
  {
    title: 'Travel Teaser',
    text: 'Discover more with SkyTrail Airlines. Book your next adventure today.'
  },
];

export default function AdminDemoAds() {
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(true);
  const [selectedVoices, setSelectedVoices] = useState([]);
  const [count, setCount] = useState(5);
  const [budget, setBudget] = useState(100);
  const [concurrency, setConcurrency] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [created, setCreated] = useState([]); // { title, adId, audioUrl, voiceLabel }

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoadingVoices(true);
        const res = await api.djVoices();
        const arr = Array.isArray(res) ? res : Array.isArray(res?.items) ? res.items : [];
        if (alive) setVoices(arr);
      } catch (e) {
        if (alive) setVoices([]);
      } finally {
        if (alive) setLoadingVoices(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const voiceOptions = useMemo(() => (
    voices.map(v => ({ value: v.id || v.voiceId || v.name, label: v.name || v.id || String(v) }))
  ), [voices]);

  const PRESETS = useMemo(() => ({
    "Warm & Friendly": ["alloy", "verse", "serene"],
    "Bold & Energetic": ["impact", "boost", "spark"],
    "Calm & Professional": ["clarity", "focus", "atlas"],
  }), []);

  function applyPreset(name) {
    const ids = PRESETS[name] || [];
    const available = new Set(voiceOptions.map(v => v.value));
    const mapped = ids.filter(id => available.has(id));
    setSelectedVoices(mapped);
  }

  async function generateOne(i) {
    const script = SAMPLE_SCRIPTS[i % SAMPLE_SCRIPTS.length];
    const voiceId = selectedVoices.length > 0
      ? selectedVoices[i % selectedVoices.length]
      : (voiceOptions[0]?.value || '');
    if (!voiceId) throw new Error('No voice selected');
    const voiceLabel = (voiceOptions.find(v => v.value === voiceId)?.label) || voiceId;

    // 1) DJ speak → get audio blob (retry with backoff)
    const speak = await withRetries(async () => {
      const r = await api.djSpeak({ text: script.text, voiceId });
      if (r?.error || !r?.blob) throw new Error('TTS failed');
      return r;
    });

    // 2) Upload blob as file
    const filename = `demo_ad_${Date.now()}_${i}.mp3`;
    const file = new File([speak.blob], filename, { type: speak.contentType || 'audio/mpeg' });
    const upRes = await withRetries(async () => {
      const r = await api.uploadFile(file);
      if (r?.error || !r?.item?.url) throw new Error('Upload failed');
      return r;
    });
    if (upRes?.error || !upRes?.item?.url) throw new Error('Upload failed');
    const audioUrl = upRes.item.url;

    // 3) Create ad record
    const ad = await withRetries(async () => await api.createAd({
      title: script.title,
      scriptText: script.text,
      voiceId,
      budget: Number(budget) || 0,
      targeting: { demo: true },
      audioUrl
    }))
    if (ad?.error || !ad?.item?._id) throw new Error('Create ad failed');

    return { title: script.title, adId: ad.item._id, audioUrl, voiceLabel };
  }

  async function onGenerate() {
    try {
      if (generating) return;
      if (!count || count < 1) {
        toast({ type: 'error', message: 'Enter a count >= 1' });
        return;
      }
      setGenerating(true);
      setCreated([]);
      const results = await runWithConcurrency(count, Math.max(1, Math.min(5, Number(concurrency) || 1)));
      toast({ type: 'success', message: `Generated ${results}/${count} ads` });
    } catch (e) {
      toast({ type: 'error', message: e?.message || 'Generation failed' });
    } finally {
      setGenerating(false);
    }
  }

  async function runWithConcurrency(n, k) {
    let completed = 0;
    let next = 0;
    async function worker() {
      while (next < n) {
        const i = next++;
        try {
          const r = await generateOne(i);
          setCreated(cur => [...cur, r]);
          completed++;
        } catch (e) {
          console.warn('[demo-ads] item failed', e);
        }
      }
    }
    const workers = Array.from({ length: k }, () => worker());
    await Promise.all(workers);
    return completed;
  }

  async function withRetries(fn, retries = 2) {
    let lastErr;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await fn();
      } catch (e) {
        lastErr = e;
        const backoff = 500 * (attempt + 1);
        await new Promise(r => setTimeout(r, backoff));
      }
    }
    throw lastErr || new Error('Operation failed');
  }

  return (
    <div className="container" style={{ padding: 24 }}>
      <h2 style={{ color: '#ff7a45' }}>Admin · Demo Ads</h2>
      <p className="muted">Generate sample advertisements using different voices to showcase the app.</p>

      <Section title="Configuration" style={{ marginBottom: 16 }}>
        <div className="row" style={{ flexWrap: 'wrap', gap: 12 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 6 }}>Voices</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {loadingVoices ? (
                <span className="muted">Loading voices…</span>
              ) : voiceOptions.length === 0 ? (
                <span className="muted">No voices available</span>
              ) : (
                voiceOptions.map(opt => (
                  <label key={opt.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={selectedVoices.includes(opt.value)}
                      onChange={(e) => {
                        setSelectedVoices(prev => e.target.checked ? [...prev, opt.value] : prev.filter(v => v !== opt.value));
                      }}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))
              )}
            </div>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6 }}>Presets</label>
            <div className="row" style={{ flexWrap: 'wrap' }}>
              {Object.keys(PRESETS).map(name => (
                <Button key={name} variant="subtle" onClick={() => applyPreset(name)}>{name}</Button>
              ))}
            </div>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, color: '#d1d5db' }}>How many</label>
            <Input
              type="number"
              min={1}
              max={50}
              value={count}
              onChange={(e) => setCount(Number(e.target.value))}
              style={{ width: 120, background: '#111827', color: '#e5e7eb', border: '1px solid #374151' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, color: '#d1d5db' }}>Budget per ad</label>
            <Input
              type="number"
              min={0}
              step={1}
              value={budget}
              onChange={(e) => setBudget(Number(e.target.value))}
              style={{ width: 140, background: '#111827', color: '#e5e7eb', border: '1px solid #374151' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, color: '#d1d5db' }}>Concurrency</label>
            <Input
              type="number"
              min={1}
              max={5}
              value={concurrency}
              onChange={(e) => setConcurrency(Number(e.target.value))}
              style={{ width: 120, background: '#111827', color: '#e5e7eb', border: '1px solid #374151' }}
            />
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <Button onClick={onGenerate} disabled={generating || loadingVoices}>
            {generating ? 'Generating…' : 'Generate Demo Ads'}
          </Button>
        </div>
      </Section>

      <Section title="Created">
        {created.length === 0 ? (
          <p className="muted">No demo ads created yet.</p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {created.map((c, idx) => (
              <li key={`${c.adId}_${idx}`} style={{ padding: '6px 4px', borderBottom: '1px solid var(--color-border)' }}>
                <div style={{ color: 'var(--color-text)' }}>{c.title} <span className="muted" style={{ fontSize: 12 }}>({c.voiceLabel})</span></div>
                <div className="muted" style={{ fontSize: 12 }}>Ad ID: {c.adId}</div>
                {c.audioUrl ? (
                  <div style={{ marginTop: 6 }}>
                    <a href={c.audioUrl} target="_blank" rel="noreferrer">Open audio</a>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Sample scripts" style={{ marginTop: 16 }}>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {SAMPLE_SCRIPTS.map((s, i) => (
            <li key={i} style={{ padding: '6px 4px', borderBottom: '1px solid var(--color-border)' }}>
              <strong>{s.title}:</strong> <span className="muted">{s.text}</span>
            </li>
          ))}
        </ul>
      </Section>
    </div>
  );
}
